import React from 'react';
import * as Sentry from '@sentry/react';
import { DropzoneArea } from 'material-ui-dropzone';
import { Box, Button, createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import { SlateResponse } from '../common/store/types';


const useStyles = makeStyles((theme: Theme) => createStyles({
  root: {
    '& .MuiDropzoneArea-root': {
      backgroundColor: '#000A16',
      borderRadius: 3,
      minHeight: '115px !important'
    },
    '& .MuiDropzoneArea-text': {
      fontSize: '14pt',
      fontWeight: 700,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      '&::after': {
        fontSize: '12pt',
        color: 'rgba(100, 100, 100, .6)',
        content: "'or drag and drop it here'",
      }
    },
    '& .MuiDropzoneArea-icon': {
      display: 'none',
    },
  },
  formWrapper: {
    width: '100%',
    color: 'white',
    // marginTop: 18,
    '& .MuiInputBase-root': {
      color: 'white',
      backgroundColor: '#000A16',
      borderRadius: 3,
      padding: 8,
      border: `1px solid rgba(238, 238, 238, .2)`,
      marginBottom: 4,
    },
    '& .MuiFormLabel-root': {
      color: 'white',
    },
    '& .MuiSvgIcon-root': {
      color: 'white',
    },
    '& button': {
      backgroundColor: '#0042C1 !important',
      padding: 8,
    },
    '& .Mui-disabled': {
      backgroundColor: '#555555 !important',
      color: '#BEBEBE'
    }
  },
  uploadCaption: {
    color: 'white',
    fontSize: 8,
  },
  subtitle: {
    lineHeight: '130%',
    opacity: '70%',
  },
  fileName: {
    // lineHeight: '130%',
    opacity: '70%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  button: {
    color: theme.palette.common.white,
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 700,
    border: `1px solid #015ACE`,
    padding: '6px 12px',
    textTransform: 'initial',
    whiteSpace: 'nowrap',
    '& svg': {
      marginLeft: '4px'
    }
  }
})
);

interface OwnProps {
  readonly onUpload: (lineups: string) => void;
  readonly onUploadSetFileName?: (fileName: string) => void;
  readonly label: string;
  readonly caption?: string;
  readonly inputId: string;
  readonly selectedSlate: SlateResponse;
  readonly defaultFileName: string;
  readonly hideDropzone?: boolean;
  readonly captionUnderneath?: boolean;
}

type Props = OwnProps;

const Upload = (props: Props) => {
  const { onUpload, onUploadSetFileName, caption, inputId, defaultFileName, hideDropzone, captionUnderneath } = props;
  const [file, setFile] = React.useState<File>();
  const [fileName, setFileName] = React.useState<string>('');
  const classes = useStyles();

  const fileReader = new FileReader();

  const handleOnChange = (e: any) => {
    setFile(e[0]);
  };

  const handleOnSubmit = () => {
    if (file) {
      fileReader.onload = function (event: any) {
        const csvOutput = event.target.result;
        onUpload(csvOutput);
        onUploadSetFileName && onUploadSetFileName(file.name);
        setFileName(file.name);
      };

      fileReader.readAsText(file);
    }
  };

  const onClear = () => {
    clearFileInput(document.getElementById(inputId))
    onUpload("");
    setFileName("");
  }

  function clearFileInput(ctrl) {
    try {
      ctrl.value = null;
    } catch (ex) {
      Sentry.captureException(ex);
     }
    if (ctrl.value) {
      ctrl.parentNode.replaceChild(ctrl.cloneNode(true), ctrl);
    }
  }

  React.useEffect(() => {
    if (file) {
      handleOnSubmit();
    }
  }, [file])

  React.useEffect(() => {
    if (!defaultFileName) {
      onClear();
    }
  }, [props.selectedSlate])

  React.useEffect(() => {
    setFileName(defaultFileName);
  }, [])

  return (
    <div className={classes.root}>
      <Box display="flex" flexDirection="row" alignItems='center'>
        {/* <Typography variant='caption' gutterBottom className={classes.formWrapper}>
          {label}
        </Typography> */}
        {
          !captionUnderneath && caption && (
            <Typography variant='caption' gutterBottom className={classes.subtitle}>
              {caption}
            </Typography>
          )
        }
        <Box display='flex' alignItems='center' overflow='hidden' pt="12px">
          <Button
            variant="outlined"
            component="label"
            className={classes.button + ' chooseFile'}
          >
            Upload Lineup File
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M7.66683 8.71667V11.5128C7.66683 11.6077 7.69867 11.687 7.76235 11.7507C7.82601 11.8143 7.90529 11.8462 8.00016 11.8462C8.09504 11.8462 8.17431 11.8143 8.23798 11.7507C8.30166 11.687 8.3335 11.6077 8.3335 11.5128V8.71667L9.4976 9.88077C9.53007 9.91325 9.56661 9.93761 9.60721 9.95385C9.64781 9.97009 9.68841 9.97757 9.72901 9.97628C9.76961 9.97501 9.81171 9.96624 9.8553 9.95C9.89889 9.93377 9.93692 9.90941 9.9694 9.87693C10.0403 9.80342 10.0765 9.72479 10.0777 9.64103C10.079 9.55727 10.0429 9.47863 9.9694 9.40513L8.37708 7.81282C8.31897 7.75469 8.25957 7.71388 8.19888 7.69038C8.1382 7.66688 8.07196 7.65513 8.00016 7.65513C7.92836 7.65513 7.86212 7.66688 7.80145 7.69038C7.74076 7.71388 7.68136 7.75469 7.62325 7.81282L6.03093 9.40513C5.96597 9.47009 5.932 9.54658 5.92901 9.63462C5.92601 9.72265 5.96127 9.80342 6.03478 9.87693C6.10828 9.94787 6.18691 9.98398 6.27068 9.98527C6.35443 9.98654 6.43307 9.95043 6.50658 9.87693L7.66683 8.71667ZM4.41041 14C4.10358 14 3.84738 13.8972 3.64183 13.6917C3.43627 13.4861 3.3335 13.2299 3.3335 12.9231V3.07692C3.3335 2.77008 3.43627 2.51389 3.64183 2.30833C3.84738 2.10278 4.10358 2 4.41041 2H9.2194C9.36299 2 9.50251 2.02906 9.63798 2.08718C9.77345 2.14529 9.88947 2.22264 9.98606 2.31923L12.3476 4.68077C12.4442 4.77736 12.5215 4.89338 12.5796 5.02885C12.6378 5.16432 12.6668 5.30384 12.6668 5.44743V12.9231C12.6668 13.2299 12.5641 13.4861 12.3585 13.6917C12.1529 13.8972 11.8967 14 11.5899 14H4.41041ZM9.3335 4.79488V2.66667H4.41041C4.30786 2.66667 4.21384 2.7094 4.12836 2.79487C4.0429 2.88034 4.00016 2.97436 4.00016 3.07692V12.9231C4.00016 13.0256 4.0429 13.1197 4.12836 13.2051C4.21384 13.2906 4.30786 13.3333 4.41041 13.3333H11.5899C11.6925 13.3333 11.7865 13.2906 11.872 13.2051C11.9574 13.1197 12.0002 13.0256 12.0002 12.9231V5.33333H9.87195C9.71725 5.33333 9.58883 5.28227 9.4867 5.18013C9.38456 5.078 9.3335 4.94958 9.3335 4.79488Z" fill="white" />
            </svg>
            <input
              id={inputId}
              type="file"
              hidden
              accept=".csv,text/csv,application/vnd.ms-excel,application/csv,text/x-csv,application/x-csv,text/plain"
              onChange={(e: any) => handleOnChange(e?.target?.files || {} as File[])}
            />
          </Button>
          {
            fileName && !captionUnderneath && (
              <Box marginLeft="8px" className={classes.fileName} fontSize="12px" onClick={() => onClear()}>
                <ClearIcon fontSize='small' />
                {fileName}
              </Box>
            )
          }
        </Box>
        {
          fileName && captionUnderneath && (
            <Box marginBottom="4px" className={classes.fileName} fontSize="12px" onClick={() => onClear()}>
              <ClearIcon fontSize='small' />
              {fileName}
            </Box>
          )
        }
        {
          captionUnderneath && !fileName && (
            <Typography variant='caption' gutterBottom className={classes.subtitle}>
              {caption}
            </Typography>
          )
        }
      </Box>
      {
        !hideDropzone && (
          <DropzoneArea
            onChange={(files) => handleOnChange(files)}
            showPreviewsInDropzone={false}
            acceptedFiles={[".csv", "text/csv", "application/vnd.ms-excel", "application/csv", "text/x-csv", "application/x-csv", "text/plain"]}
            dropzoneText={"Select a CSV file to upload"}
            Icon={undefined}
            filesLimit={1}
            showAlerts={false}
          />
        )
      }
    </div>
  );
};

export default Upload